import { db } from '../lib/database';
import { apiClient } from './apiClient';
import { setSyncInProgress, updatePendingCount, setLastSyncTime, setSyncError } from '../reducers/syncSlice';
import { toastService } from './toastService';
import { networkService } from './networkService';
import { API_ENDPOINTS } from '../config/api';

let storeInstance;

export const injectStore = store => {
  storeInstance = store;
};

class SyncServiceController {
  constructor() {
    this.isSyncing = false;
  }

  /**
   * Count logical pending items.
   * An offline entity creation + its linked form submission are one workflow,
   * so they count as a single pending item rather than two.
   */
  _countPendingItems(items) {
    // Collect all temp entity IDs that have a dependent submission in this queue.
    const entityIdsWithSubmission = new Set(
      items
        .filter(i => i.type === 'submission' && i.payload?.entity_id)
        .map(i => i.payload.entity_id)
    );

    const unique = new Set();
    items.forEach(item => {
      if (item.type === 'file_upload') {
        // File uploads don't count as separate logical items.
        return;
      }
      if (item.type === 'entity') {
        // Skip counting the entity outbox entry on its own if a submission
        // already references it — they're the same logical pending item.
        if (entityIdsWithSubmission.has(item.tempId)) return;
        // Standalone entity creation (no linked submission yet).
        unique.add(item.tempId || item.id);
        return;
      }
      // For submissions (and any unknown types) use the submission tempId.
      const subId = item.type === 'submission' || !item.type
        ? item.tempId
        : item.payload?.submissionId;
      unique.add(subId || item.id);
    });
    return unique.size;
  }

  addToOutbox(request) {
    return db.outbox
      .add({
        ...request,
        timestamp: new Date().toISOString(),
        retryCount: 0
      })
      .then(() => {
        if (storeInstance) {
          return db.outbox.toArray().then(items => {
            storeInstance.dispatch(updatePendingCount(this._countPendingItems(items)));
          });
        }
      });
  }

  processOutbox(itemsToSync, progressCallback, showToasts = false, skipInitialToast = false, existingToastId = null) {
    if (this.isSyncing || networkService.getNetworkQuality() === 'offline' || !storeInstance) {
      return this._syncPendingFiles().then(() => []);
    }

    const getQueue = itemsToSync ? Promise.resolve(itemsToSync) : db.outbox.toArray();

    return getQueue.then(queue => {
      if (queue.length === 0) {
        return this._syncPendingFiles().then(() => []);
      }

      this.isSyncing = true;
      storeInstance.dispatch(setSyncInProgress(true));

      let syncToastId = existingToastId;
      if (showToasts && !skipInitialToast && !syncToastId) {
        syncToastId = toastService.loading('Sync started');
      }

      // Entity items MUST be processed before submissions so that temp entity IDs
      // are resolved to real UUIDs before dependent submissions are sent.
      const entityItems = queue.filter(item => item.type === 'entity');
      const submissionItems = queue.filter(item => item.type !== 'file_upload' && item.type !== 'entity');
      const fileUploadItems = queue.filter(item => item.type === 'file_upload');

      let syncedCount = 0;
      let rejectedCount = 0;
      let failedCount = 0;

      const processItems = (items, offset = 0) => {
        return items.reduce((promise, item, index) => {
          return promise.then(() => {
            if (progressCallback) {
              progressCallback({
                current: offset + index + 1,
                total: queue.length,
                currentItem: `Uploading your data (${offset + index + 1} of ${queue.length})`
              });
            }

            const processPromise = item.type === 'file_upload' ? this._syncFileUpload(item) : this._syncItem(item);

            return processPromise
              .then(() => {
                syncedCount++;
              })
              .catch(error => {
                const isPermanent = this._isPermanentError(error);
                const errors = error.response?.data?.errors;
                const isFieldError = (error.response?.status === 400 || error.response?.status === 422) && errors && Object.keys(errors).some(key => key.includes('form_field_id'));

                if (isPermanent) {
                  return this._removePermanently(item, error).then(() => {
                    rejectedCount++;
                  });
                } else if (isFieldError && item.type === 'submission') {
                  return this._handleFieldError(item, error).then(() => {
                    failedCount++;
                  });
                } else {
                  return this._handleRetry(item, error).then(() => {
                    failedCount++;
                  });
                }
              });
          });
        }, Promise.resolve());
      };

      return processItems(entityItems, 0)
        .then(() => processItems(submissionItems, entityItems.length))
        .then(() => processItems(fileUploadItems, entityItems.length + submissionItems.length))
        .then(() => this._syncPendingFiles())
        .then(() => this._updateUI(syncedCount, rejectedCount, failedCount, showToasts, syncToastId))
        .then(() => {
          this.isSyncing = false;
          // We don't dismiss syncToastId here because _updateUI updates it to success/error, 
          // and it will auto-dismiss based on the duration we pass during update.
          return syncedCount;
        });
    });
  }

  _syncItem(item) {
    const cleanPayload = { ...item.payload };
    const tempId = item.tempId;
    delete cleanPayload.tempId;

    // If this submission references an entity that hasn't synced yet (still a temp ID),
    // defer it — the entity sync will rewrite entity_id in the outbox once complete.
    if (item.type === 'submission' && cleanPayload.entity_id && String(cleanPayload.entity_id).startsWith('temp_')) {
      return Promise.resolve(); // Skip — outbox entry is NOT deleted, will retry next cycle
    }

    return apiClient
      .request({
        method: item.method || 'POST',
        url: item.endpoint,
        data: cleanPayload,
        timeout: 300000,
        suppressToast: true
      })
      .then(response => {
        if (tempId && response?.data?.data?.id) {
          const actualId = response.data.data.id;

          // Handle Entity Sync
          if (item.type === 'entity') {
            return db.entities.get(tempId).then(tempEntity => {
              if (tempEntity) {
                return db.entities
                  .delete(tempId)
                  .then(() => db.entities.put({ ...tempEntity, id: actualId, syncStatus: 'synced' }))
                  .then(() => {
                    // Update any pending submissions in outbox that use this temp entity ID
                    return db.outbox
                      .where('type')
                      .equals('submission')
                      .toArray()
                      .then(pendingSubmissions => {
                        const updates = pendingSubmissions
                          .filter(sub => sub.payload?.entity_id === tempId)
                          .map(sub => {
                            const updatedPayload = { ...sub.payload, entity_id: actualId };
                            return db.outbox.update(sub.id, { payload: updatedPayload });
                          });
                        return Promise.all(updates);
                      });
                  });
              }
            });
          }

          // Handle Submission Sync
          return db.submissions.get(tempId).then(tempSubmission => {
            if (tempSubmission) {
              return db.submissions
                .delete(tempId)
                .then(() => db.file_uploads.where('submission_id').equals(tempId).modify({ submission_id: actualId }))
                .then(updatedFiles => {
                  if (updatedFiles > 0) {
                    return db.file_uploads
                      .where('submission_id')
                      .equals(actualId)
                      .and(file => file.sync_status === 'pending')
                      .toArray()
                      .then(pendingFiles => {
                        if (pendingFiles.length > 0) {
                          return this._syncSubmissionFiles(actualId).catch(() => {
                            return db.outbox
                              .where('type')
                              .equals('file_upload')
                              .and(i => i.payload?.submissionId === actualId)
                              .first()
                              .then(existingEntry => {
                                if (!existingEntry) {
                                  const fieldTypes = {};
                                  pendingFiles.forEach(file => {
                                    fieldTypes[file.field_name] = file.field_type || 'FILE';
                                  });
                                  return this.addToOutbox({
                                    endpoint: API_ENDPOINTS.submissions.uploadFiles(actualId),
                                    method: 'POST',
                                    payload: { submissionId: actualId, fieldTypes },
                                    description: `Upload files for submission #${actualId}`,
                                    type: 'file_upload'
                                  });
                                }
                              });
                          });
                        }
                      });
                  }
                });
            }
          });
        }
        return response;
      })
      .then(() => db.outbox.delete(item.id));
  }

  _syncFileUpload(item) {
    const { submissionId, fieldTypes = {} } = item.payload;

    if (submissionId.startsWith('temp_')) {
      return db.outbox.delete(item.id);
    }

    return db.file_uploads
      .where('submission_id')
      .equals(submissionId)
      .and(file => file.sync_status === 'pending')
      .toArray()
      .then(storedFiles => {
        if (storedFiles.length === 0) {
          return db.outbox.delete(item.id);
        }

        return db.submissions.get(submissionId).then(submission => {
          if (!submission || submission.syncStatus !== 'synced') {
            return Promise.resolve();
          }

          const groupedFiles = storedFiles.reduce((acc, file) => {
            if (!acc[file.field_name]) acc[file.field_name] = [];
            acc[file.field_name].push(file);
            return acc;
          }, {});

          const uploads = Object.entries(groupedFiles).map(([fieldName, files]) => {
            const formData = new FormData();
            formData.append('submission_id', submissionId);
            formData.append('field_id', files[0]?.field_id || fieldName);
            formData.append('field_name', fieldName);
            formData.append('field_type', files[0]?.field_type || fieldTypes[fieldName] || 'FILE');

            files.forEach(file => {
              const blob = new Blob([file.file_data], { type: file.file_type });
              const fileObj = new File([blob], file.file_name, { type: file.file_type });
              formData.append('files[]', fileObj);
            });

            return apiClient
              .post(API_ENDPOINTS.submissions.uploadFiles(submissionId), formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                timeout: 300000,
                suppressToast: true
              })
              .then(() => {
                const fileIds = files.map(f => f.id);
                return db.file_uploads.bulkUpdate(
                  fileIds.map(id => ({
                    key: id,
                    changes: { sync_status: 'synced', synced_at: new Date().toISOString() }
                  }))
                );
              });
          });

          return Promise.all(uploads).then(() => db.outbox.delete(item.id));
        });
      });
  }

  _syncSubmissionFiles(submissionId) {
    return db.file_uploads
      .where('submission_id')
      .equals(submissionId)
      .and(file => file.sync_status === 'pending')
      .toArray()
      .then(pendingFiles => {
        if (pendingFiles.length === 0) return;

        const groupedFiles = pendingFiles.reduce((acc, file) => {
          if (!acc[file.field_name]) acc[file.field_name] = [];
          acc[file.field_name].push(file);
          return acc;
        }, {});

        const uploads = Object.entries(groupedFiles).map(([fieldName, files]) => {
          const formData = new FormData();
          formData.append('submission_id', submissionId);
          formData.append('field_id', files[0]?.field_id || fieldName);
          formData.append('field_name', fieldName);
          formData.append('field_type', files[0]?.field_type || 'FILE');

          files.forEach(file => {
            const blob = new Blob([file.file_data], { type: file.file_type });
            const fileObj = new File([blob], file.file_name, { type: file.file_type });
            formData.append('files[]', fileObj);
          });

          return apiClient
            .post(API_ENDPOINTS.submissions.uploadFiles(submissionId), formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 300000,
              suppressToast: true
            })
            .then(() => {
              const fileIds = files.map(f => f.id);
              return db.file_uploads.bulkUpdate(
                fileIds.map(id => ({
                  key: id,
                  changes: { sync_status: 'synced', synced_at: new Date().toISOString() }
                }))
              );
            });
        });

        return Promise.all(uploads);
      });
  }

  _syncPendingFiles() {
    return import('./fileUploadService').then(({ fileUploadService }) => {
      return db.file_uploads
        .where('sync_status')
        .equals('pending')
        .toArray()
        .then(pendingFiles => {
          if (pendingFiles.length === 0) return;

          const submissionIds = [...new Set(pendingFiles.map(f => f.submission_id))];

          return submissionIds.reduce((promise, subId) => {
            return promise.then(() => {
              return db.submissions.get(subId).then(submission => {
                if (submission && submission.syncStatus === 'synced') {
                  return fileUploadService.syncPendingUploads(subId);
                }
              });
            });
          }, Promise.resolve());
        });
    });
  }

  _isPermanentError(error) {
    if (!error.response) return false;
    const status = error.response.status;
    const message = error.response.data?.message?.toLowerCase() || '';
    const errors = error.response.data?.errors;

    if (status === 401 || status === 403 || status === 404) return true;
    if (message.includes('draft') || message.includes('deleted')) return true;

    // Special handling for validation errors:
    // If it's a form_field_id error, it's likely a schema mismatch (admin deleted field).
    // We return FALSE here so it goes to _handleFieldError instead of being removed immediately.
    if (status === 400 || status === 422) {
      if (errors && Object.keys(errors).some(key => key.includes('form_field_id'))) {
        return false;
      }
      if (message.includes('form field') || message.includes('form_field')) return false;

      // Other validation errors might be permanent
      if (message.includes('invalid') && !message.includes('field')) return true;
      return false;
    }

    return false;
  }

  _removePermanently(item, error) {
    return db.outbox
      .delete(item.id)
      .then(() => {
        if (item.tempId) {
          return db.submissions.delete(item.tempId);
        }
      })
      .then(() => {
        const message = this._getErrorMessage(error);
        toastService.error(message, { autoClose: 5000 });
      });
  }

  _handleFieldError(item, error) {
    const formId = item.payload?.form_id;
    const tempId = item.tempId;

    if (!formId) {
      return this._handleRetry(item, error);
    }

    // Attempt to heal the submission by fetching latest form definition
    if (tempId) {
      return db.submissions.get(tempId).then(submission => {
        if (!submission || submission.syncStatus === 'synced') {
          return db.outbox.delete(item.id);
        }

        return import('./formsService').then(({ formsService }) => {
          // Fetch fresh form structure from API
          return formsService
            .getById(formId, false)
            .then(response => {
              const formData = response.data?.data || response.data;
              if (!formData || !formData.sections) {
                // Failed to get form definition, just retry later
                return this._handleRetry(item, error);
              }

              // Identify currently valid field IDs
              const validFieldIds = new Set();
              formData.sections.forEach(section => {
                if (section.fields) {
                  section.fields.forEach(field => {
                    validFieldIds.add(field.id);
                  });
                }
              });

              // Filter out answers for fields that no longer exist
              const cleanedAnswers = item.payload.answers.filter(answer => validFieldIds.has(answer.form_field_id));

              if (cleanedAnswers.length === 0) {
                // If literally all fields are gone, we probably can't submit
                return this._removePermanently(item, {
                  response: {
                    data: { message: 'All form fields have been removed from this form' }
                  }
                });
              }

              // Update local submission to reflect the cleaned state (so user sees what was actually synced)
              const cleanedLocalAnswers = Object.entries(submission.answers || {})
                .filter(([fieldId]) => validFieldIds.has(fieldId))
                .reduce((acc, [key, value]) => {
                  acc[key] = value;
                  return acc;
                }, {});

              return db.submissions.update(tempId, { answers: cleanedLocalAnswers }).then(() => {
                // Re-queue the cleaned item for immediate retry
                const updatedItem = {
                  ...item,
                  payload: {
                    ...item.payload,
                    answers: cleanedAnswers
                  },
                  retryCount: 0, // Reset retry count
                  lastError: 'Invalid fields removed - retrying',
                  nextRetryAt: new Date(Date.now() + 2000).toISOString()
                };
                return db.outbox.put(updatedItem);
              });
            })
            .catch(() => {
              // If fetching form fails, just standard retry
              return this._handleRetry(item, error);
            });
        });
      });
    }

    return this._handleRetry(item, error);
  }

  _handleRetry(item, error) {
    const retryCount = (item.retryCount || 0) + 1;
    const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;

    const isFileUpload = item.type === 'file_upload';
    const retryDelay = isFileUpload ? retryCount * 10000 : retryCount * 5000;

    return db.outbox.put({
      ...item,
      retryCount,
      lastError: errorMessage,
      nextRetryAt: new Date(Date.now() + retryDelay).toISOString()
    });
  }

  _getErrorMessage(error) {
    const apiMessage = error.response?.data?.message || error.message || '';
    const message = apiMessage.toLowerCase();

    if (message.includes('draft')) return `Sync failed: ${apiMessage}`;
    if (message.includes('deleted')) return `Sync failed: ${apiMessage}`;
    if (error.response?.status === 401) return 'Authentication failed - Please login again';
    if (error.response?.status === 413) return 'File too large - Maximum file size exceeded';
    if (apiMessage) return `Sync failed: ${apiMessage}`;

    return 'Submission could not be synced - Will retry automatically';
  }

  _updateUI(syncedCount, rejectedCount, failedCount, showToasts = false, toastId = null) {
    if (syncedCount > 0) {
      const now = new Date().toISOString();
      storeInstance.dispatch(setLastSyncTime(now));
      db.settings.put({ key: 'lastSyncTime', value: now });
    }

    if (showToasts) {
      const totalProcessed = syncedCount + rejectedCount + failedCount;
      if (totalProcessed > 0) {
        if (failedCount === 0 && rejectedCount === 0) {
          toastService.success(syncedCount === 1 ? '1 submission uploaded successfully' : `All ${syncedCount} submissions uploaded successfully`, { id: toastId });
        } else {
          const parts = [];
          if (syncedCount > 0) parts.push(`${syncedCount} uploaded`);
          if (failedCount > 0) parts.push(`${failedCount} waiting to retry`);
          if (rejectedCount > 0) parts.push(`${rejectedCount} need your attention`);

          const summaryMsg = `Upload complete: ${parts.join(', ')}`;
          if (failedCount > 0 || rejectedCount > 0) {
            toastService.warning(summaryMsg, { duration: 5000, id: toastId });
          } else {
            toastService.success(summaryMsg, { id: toastId });
          }
        }
      } else if (toastId) {
        // Dismiss loading toast if no items were actually processed
        toastService.dismiss(toastId);
      }
    }

    if (failedCount > 0) {
      storeInstance.dispatch(setSyncError(`${failedCount} submissions need your attention`));
    } else {
      storeInstance.dispatch(setSyncError(null));
    }

    return db.outbox.toArray().then(items => {
      storeInstance.dispatch(updatePendingCount(this._countPendingItems(items)));
    });
  }

  getSyncStats() {
    return Promise.all([db.outbox.toArray(), db.outbox.where('retryCount').above(0).count()]).then(([items, failedItems]) => {
      const lastSyncTime = storeInstance?.getState()?.sync?.lastSyncTime;
      return { pendingCount: this._countPendingItems(items), failedItems, lastSyncTime, isSyncing: this.isSyncing };
    });
  }

  reportOfflineStats() {
    return db.outbox.toArray().then(items => {
      const offlineSubmissions = items.filter(item => item.type === 'submission');
      if (offlineSubmissions.length === 0) return Promise.resolve();

      const counts = {};
      offlineSubmissions.forEach(item => {
        const formId = item.payload?.form_id;
        if (formId) {
          counts[formId] = (counts[formId] || 0) + 1;
        }
      });

      if (Object.keys(counts).length === 0) return Promise.resolve();

      const payload = {
        submissions: Object.keys(counts).map(form_id => ({
          form_id,
          submission_count: counts[form_id]
        })),
        submission_mode: 'offline'
      };

      return apiClient.post(API_ENDPOINTS.submissions.offlineSync, payload, { suppressToast: true })
        .catch(err => {
          console.error('Failed to report offline stats:', err);
        });
    });
  }

  clearOutbox() {
    return db.outbox.clear().then(() => {
      if (storeInstance) {
        storeInstance.dispatch(updatePendingCount(0));
      }
    });
  }

  forceSyncItem(itemId) {
    return db.outbox.get(itemId).then(item => {
      if (!item) throw new Error('Item not found');
      return this.processOutbox([item], null, false);
    });
  }

  syncAllPendingData() {
    if (this.isSyncing || networkService.getNetworkQuality() === 'offline') {
      return Promise.resolve({ success: false, message: 'Sync already in progress or offline' });
    }
    return this.processOutbox(null, null, false)
      .then(() => import('./fileUploadService'))
      .then(({ fileUploadService }) => fileUploadService.syncPendingUploads())
      .then(() => ({ success: true, message: 'All data synced successfully' }))
      .catch(error => ({ success: false, message: error.message }));
  }
}

export const syncService = new SyncServiceController();
